/**
 * „Erster Bildschirm im Vergleich" im Bericht (Werkzeug test_first_screen,
 * _shared/sim/erster_bildschirm.ts, seit 09.10.2026).
 *
 * Die Nutzlast kommt aus `ersterBildschirmFuerBericht` (bericht_design.ts):
 * je Variante Name, Adresse, Klickanteil, Kern, Sicherheit, wie viele
 * verstanden haben, was angeboten wird, und die Bildpfade, die `report` bei
 * jedem Abruf neu signiert (`bild_links`). Keine Stimmen im Wortlaut, wie im
 * Design-Kapitel. Bewusst knapp: die Stimmen und Gründe zu zögern stehen im
 * Gespräch mit Claude, der Bericht zeigt Bild und Zahl.
 */

import { sichererLink } from './sicherer-link';
import { sprache, t } from './sprache';

export interface ErsteVariante {
  nummer: number;
  name?: string;
  url?: string;
  zuerst_geklickt: number;
  anteil: number;
  kern?: { zuerst_geklickt: number; von: number };
  sicherheit: number | null;
  wuerden_wirklich_klicken: number;
  verstanden_klar: number;
  /** Seit erster@2: wer bei dieser Fassung bleibt (klicken oder weiterlesen) und wer abspringt. */
  bleiben?: { bleiben: number; abspringen: number; von: number };
  kern_bleiben?: { bleiben: number; abspringen: number; von: number };
  bild_links?: { desktop?: string; mobil?: string };
}

export interface ErsterTest {
  am?: string;
  personas: number;
  ansichten?: string[];
  handy_breite?: number;
  herkunft?: string;
  urteil?: { fuehrt: number | null; klar: boolean; satz: string };
  varianten: ErsteVariante[];
}

const komma = (x: number) => (sprache() === 'en' ? String(x) : String(x).replace('.', ','));

export function varianteTitel(v: ErsteVariante): string {
  return t(`Variante ${v.nummer}${v.name ? ` „${v.name}“` : ''}`, `Variant ${v.nummer}${v.name ? ` “${v.name}”` : ''}`);
}

/** Eine Zeile Zahlen je Variante, in der Reihenfolge, in der man sie liest. */
export function varianteZeile(v: ErsteVariante, personas: number): string {
  if (sprache() === 'en') {
    return [
      `clicked first ${v.zuerst_geklickt} of ${personas} (${Math.round(v.anteil * 100)}%)`,
      v.kern ? `core ${v.kern.zuerst_geklickt} of ${v.kern.von}` : '',
      v.sicherheit != null ? `confidence ${komma(v.sicherheit)} of 5` : '',
      `clear what is offered: ${v.verstanden_klar} of ${personas}`,
      bleibenText(v),
    ].filter(Boolean).join(' · ');
  }
  return [
    `zuerst geklickt ${v.zuerst_geklickt} von ${personas} (${Math.round(v.anteil * 100)} %)`,
    v.kern ? `Kern ${v.kern.zuerst_geklickt} von ${v.kern.von}` : '',
    v.sicherheit != null ? `Sicherheit ${komma(v.sicherheit)} von 5` : '',
    `klar, was angeboten wird: ${v.verstanden_klar} von ${personas}`,
    bleibenText(v),
  ].filter(Boolean).join(' · ');
}

/** „6 von 8 bleiben, 2 springen ab“, im Kern, wenn es ihn gibt. Ohne Angabe leer. */
export function bleibenText(v: Pick<ErsteVariante, 'bleiben' | 'kern_bleiben'>): string {
  const b = v.kern_bleiben ?? v.bleiben;
  if (!b || !b.von) return '';
  return t(
    `${b.bleiben} von ${b.von}${v.kern_bleiben ? ' im Kern' : ''} bleiben, ${b.abspringen} ${b.abspringen === 1 ? 'springt' : 'springen'} ab`,
    `${b.bleiben} of ${b.von}${v.kern_bleiben ? ' in the core' : ''} ${b.bleiben === 1 ? 'stays' : 'stay'}, ${b.abspringen} ${b.abspringen === 1 ? 'bounces' : 'bounce'}`,
  );
}

export function ersterBildschirmTitel(test: ErsterTest): string {
  return t(
    `${test.varianten.length} erste Bildschirme, ${test.personas} Personas`,
    `${test.varianten.length} first ${test.varianten.length === 1 ? 'screen' : 'screens'}, ${test.personas} ${test.personas === 1 ? 'persona' : 'personas'}`,
  );
}

// Die Eigenschaft heißt `t` (Aufrufer); hier `test`, damit `t` die Übersetzung bleibt.
export function ErsterBildschirm({ t: test }: { t: ErsterTest }) {
  const varianten = Array.isArray(test?.varianten) ? test.varianten : [];
  return (
    <div className="break-inside-avoid">
      {test.urteil?.satz && <p className="mt-0 mb-2 max-w-[72ch] text-[1.0625rem] leading-relaxed text-[#1d1f26]">{test.urteil.satz}</p>}
      <p className="mt-0 mb-5 max-w-[72ch] text-[0.9375rem] leading-relaxed text-[#2f3238]">
        {t('Jede Persona sah von allen Varianten, was vor dem Scrollen zu sehen ist (Above the Fold), nebeneinander, in wechselnder Reihenfolge und ohne Namen, und entschied, wo sie zuerst klickt.',
          'Each persona saw what every variant shows before scrolling (above the fold), side by side, in changing order and without names, and decided where to click first.')}
        {test.herkunft ? t(` Herkunft der Besucher: ${test.herkunft}`, ` Where visitors come from: ${test.herkunft}`) : ''}
      </p>
      <div className={`grid gap-4 ${varianten.length > 2 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2'}`}>
        {varianten.map((v) => {
          const bild = v.bild_links?.mobil ?? v.bild_links?.desktop;
          const link = sichererLink(v.url ?? '');
          const vorn = test.urteil?.fuehrt === v.nummer;
          return (
            <figure key={v.nummer} className={`m-0 overflow-hidden rounded-xl border bg-[#ffffff] break-inside-avoid ${vorn ? 'border-[#1d1f26]' : 'border-[#dde3e1]'}`}>
              {bild && <img src={bild} alt={t(`Erster Bildschirm von ${varianteTitel(v)}`, `First screen of ${varianteTitel(v)}`)} className="block max-h-[28rem] w-full border-b border-[#e8eceb] bg-[#eef2f1] object-contain object-top" />}
              <figcaption className="px-4 py-3 text-[0.9375rem] leading-snug">
                <span className="block font-semibold text-[#1d1f26]">{varianteTitel(v)}{vorn ? ` · ${test.urteil?.klar ? t('vorn', 'ahead') : t('knapp vorn', 'narrowly ahead')}` : ''}</span>
                <span className="mt-1 block text-[#2f3238] tabular-nums">{varianteZeile(v, test.personas)}</span>
                {link && <a href={link} className="mt-1 block truncate text-[0.8125rem] text-[#1f6964]">{v.url}</a>}
              </figcaption>
            </figure>
          );
        })}
      </div>
      <p className="mt-4 mb-0 text-[0.8125rem] leading-relaxed text-[#5f646b]">
        {t('Simuliert und nicht kalibriert: ein erster Eindruck, keine Trefferquote. Echte Besucher sehen nur eine Variante.',
          'Simulated and not calibrated: a first impression, not a hit rate. Real visitors see only one variant.')}
      </p>
    </div>
  );
}
